import type { DashboardTile } from './dvm-layout-builder.types';
import { DASH_COLS, clampNumber } from './dvm-dashboard-layout-json.utils';

/**
 * Pure helpers for the canvas tile list and its GROUP cards (a group card is a
 * tile whose `groupMembers` are the grouped tiles). Every function returns a
 * new list / member array instead of mutating, except where noted.
 */

/** Top-level tiles with every group card replaced by its members. */
export function flattenTiles(tiles: DashboardTile[]): DashboardTile[] {
  const out: DashboardTile[] = [];
  tiles.forEach((t) => {
    if (t.layoutType === 'GROUP') { if (t.groupMembers) out.push(...t.groupMembers); return; }
    out.push(t);
  });
  return out;
}

export function findGroupMemberLocation(tiles: DashboardTile[], id: string): { group: DashboardTile; index: number } | null {
  for (const t of tiles) {
    if (t.layoutType !== 'GROUP' || !t.groupMembers) continue;
    const index = t.groupMembers.findIndex((m) => m.id === id);
    if (index >= 0) return { group: t, index };
  }
  return null;
}

/** A tile by id, top-level or inside a group card. */
export function findTileAnywhere(tiles: DashboardTile[], id: string): DashboardTile | undefined {
  const top = tiles.find((t) => t.id === id);
  if (top) return top;
  return findGroupMemberLocation(tiles, id)?.group.groupMembers?.find((m) => m.id === id);
}

/**
 * Replace the tile with `next.id` wherever it sits. A group member is swapped
 * inside its group (the group's member array is replaced). Returns the new
 * top-level list, or null when the id is unknown.
 */
export function replaceTileInPlace(tiles: DashboardTile[], next: DashboardTile): DashboardTile[] | null {
  const topIndex = tiles.findIndex((t) => t.id === next.id);
  if (topIndex >= 0) {
    const updated = [...tiles];
    updated[topIndex] = next;
    return updated;
  }
  const loc = findGroupMemberLocation(tiles, next.id);
  if (!loc) return null;
  const members = [...(loc.group.groupMembers || [])];
  members[loc.index] = next;
  loc.group.groupMembers = members;
  return [...tiles];
}

/** A new GROUP card holding copies of `selected` (width = widest member, height = sum). */
export function buildGroupTile(selected: DashboardTile[], componentId: string, format: Record<string, unknown>): DashboardTile {
  const innerCols = clampNumber(Math.max(...selected.map((t) => t.colSpan)), 2, DASH_COLS);
  return {
    id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    componentId,
    layoutType: 'GROUP',
    title: 'Grouped card',
    groupTitle: 'Grouped card',
    colSpan: innerCols,
    rowSpan: selected.reduce((sum, t) => sum + t.rowSpan, 0),
    format,
    payload: {},
    groupMembers: selected.map((t) => ({ ...t })),
  };
}

/** Drop the tiles in `ids` and put `group` where the first of them was. */
export function groupTiles(tiles: DashboardTile[], ids: ReadonlySet<string>, group: DashboardTile): DashboardTile[] {
  let inserted = false;
  const next: DashboardTile[] = [];
  tiles.forEach((t) => {
    if (!ids.has(t.id)) { next.push(t); return; }
    if (!inserted) { next.push(group); inserted = true; }
  });
  return next;
}

/** Replace a group card by its members, in place. Null when `tile` is not a group in the list. */
export function ungroupTiles(tiles: DashboardTile[], tile: DashboardTile): DashboardTile[] | null {
  if (tile.layoutType !== 'GROUP' || !tile.groupMembers?.length) return null;
  const index = tiles.findIndex((t) => t.id === tile.id);
  if (index === -1) return null;
  return [...tiles.slice(0, index), ...tile.groupMembers, ...tiles.slice(index + 1)];
}

export function canMoveGroupMember(members: DashboardTile[] | undefined, member: DashboardTile, direction: -1 | 1): boolean {
  if (!members?.length) return false;
  const index = members.findIndex((m) => m.id === member.id);
  if (index === -1) return false;
  const target = index + direction;
  return target >= 0 && target < members.length;
}

/** Members with `member` swapped one position in `direction`; null when it cannot move. */
export function moveGroupMember(members: DashboardTile[] | undefined, member: DashboardTile, direction: -1 | 1): DashboardTile[] | null {
  if (!members?.length) return null;
  const from = members.findIndex((m) => m.id === member.id);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= members.length) return null;
  const next = [...members];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Inline styles of a group card from its Format (border colour/width, background, radius, transparent). */
export function groupCardStyleMap(format: Record<string, unknown> | undefined): Record<string, string> {
  const styles: Record<string, string> = {};
  if (!format) return styles;
  const transparent = format['cardTransparent'] === true || format['cardTransparent'] === 'true';
  const border = String(format['cardBorderColor'] ?? '').trim();
  const width = Number(format['cardBorderWidth']);
  const background = String(format['cardBackground'] ?? '').trim();
  const radius = Number(format['cardCornerRadius']);

  if (transparent) {
    styles['background'] = 'transparent';
    styles['border-color'] = 'transparent';
    styles['box-shadow'] = 'none';
  } else {
    if (background) styles['background'] = background;
    if (border) styles['border-color'] = border;
  }
  if (Number.isFinite(width)) styles['border-width'] = `${Math.max(0, width)}px`;
  if (Number.isFinite(radius)) styles['border-radius'] = `${Math.max(0, radius)}px`;
  return styles;
}
